// Public entry: analyzePedigree(members, opts) → AnalysisResult.
//
// Pure, synchronous, no I/O. `opts.carrierStatusByMember` is the byMember map
// from the shipped deriveCarrierStatus (reused, not recomputed — plan §12.2);
// `opts.profileMatch` is the client-side Phenomizer Profile Match for
// cross-evidence. Both optional; the analyzer degrades gracefully without them.

import { tokenisePedigree } from './tokenise';
import {
  detectInheritancePatterns,
  bestInheritancePattern
} from './detectors/inheritance';
import { detectSyndromes } from './detectors/syndromes';
import { detectCascadeCandidates } from './detectors/cascade';
import { detectAlerts } from './detectors/alerts';
import { detectCrossEvidence } from './detectors/crossEvidence';
import { buildIndividualSections } from './individualLens';
import { CONFIDENCE, PATTERN } from './constants';
import { RULESET_VERSION } from './rulesetVersion';

const PATTERN_SHORT = {
  [PATTERN.AD]: 'AD',
  [PATTERN.AR]: 'AR',
  [PATTERN.XLD]: 'XLD',
  [PATTERN.XLR]: 'XLR',
  [PATTERN.MT]: 'MT',
  [PATTERN.YL]: 'YL'
};
const RANK = { strong: 3, moderate: 2, weak: 1, ruled_out: 0, none: 0 };

function buildPatternStrip(patterns, syndromes, cascade) {
  const best = [...patterns]
    .filter((p) => p.confidence !== CONFIDENCE.none && !p.ruledOut)
    .sort((a, b) => RANK[b.confidence] - RANK[a.confidence])[0];
  const topSyndrome = [...syndromes].sort(
    (a, b) => RANK[b.confidence] - RANK[a.confidence]
  )[0];
  return {
    inheritanceLabel: best ? PATTERN_SHORT[best.id] || null : null,
    syndromeLabel: topSyndrome ? topSyndrome.label : null,
    cascadeCount: cascade.length
  };
}

// Rough generation count = longest parent chain depth + 1.
function estimateGenerations(ped) {
  const depthCache = new Map();
  const depth = (pid, seen = new Set()) => {
    if (depthCache.has(pid)) return depthCache.get(pid);
    if (seen.has(pid)) return 0;
    seen.add(pid);
    const ind = ped.byPid.get(pid);
    if (!ind) return 0;
    const parents = [ind.fatherId, ind.motherId].filter(
      (id) => id && ped.byPid.has(id)
    );
    const d = parents.length
      ? 1 + Math.max(...parents.map((p) => depth(p, seen)))
      : 0;
    depthCache.set(pid, d);
    return d;
  };
  let max = 0;
  for (const ind of ped.individuals) max = Math.max(max, depth(ind.pid));
  return max + 1;
}

export function analyzePedigree(members = [], opts = {}) {
  const {
    carrierStatusByMember = {},
    profileMatch = null,
    conditionCarrierEnabled = false
  } = opts;
  const ped = tokenisePedigree(members, {
    carrierStatusByMember,
    conditionCarrierEnabled
  });

  // Per-disease inheritance — the signal Phenomizer Phase C consumes.
  const inheritanceByDisease = {};
  const inheritancePatterns = [];
  const allPatternsByDisease = {};
  for (const disease of ped.familyDiseases) {
    allPatternsByDisease[disease.id] = detectInheritancePatterns(
      ped,
      disease.id
    );
    const best = bestInheritancePattern(ped, disease.id);
    if (best) {
      inheritanceByDisease[disease.id] = best;
      inheritancePatterns.push(best);
    }
  }

  const syndromes = detectSyndromes(ped);

  const cascadeCandidates = ped.familyDiseases.flatMap((d) =>
    detectCascadeCandidates(ped, d.id, inheritanceByDisease[d.id]).map((c) => ({
      ...c,
      diseaseId: d.id
    }))
  );

  const alerts = detectAlerts(ped);

  const family = {
    inheritancePatterns,
    inheritanceByDisease, // per-disease best pattern (for Phase C weighting)
    allPatternsByDisease, // full detector output per disease (audit/debug)
    syndromes,
    cascadeCandidates,
    alerts,
    crossEvidence: [],
    patternStrip: buildPatternStrip(
      inheritancePatterns,
      syndromes,
      cascadeCandidates
    )
  };

  // Cross-evidence needs the family result + the (optional) Profile Match.
  family.crossEvidence = profileMatch
    ? detectCrossEvidence({ family }, profileMatch)
    : [];

  const individuals = buildIndividualSections(ped, family);

  const generations = estimateGenerations(ped);
  return {
    meta: {
      rulesetVersion: RULESET_VERSION,
      structureHash: ped.structureHash,
      generatedAt: new Date().toISOString(),
      inputsSummary: {
        individuals: ped.individuals.filter((i) => !i.isDummy).length,
        generations,
        diseases: ped.familyDiseases.length
      }
    },
    family,
    individuals
  };
}
